import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

const QUERY_KEY = "teamIds";
const MAX_TEAM_IDS = 20;

const normalizeTeamIds = (ids: string[] | null | undefined): string[] =>
  [
    ...new Set((ids || []).map((id) => String(id).trim()).filter(Boolean)),
  ].slice(0, MAX_TEAM_IDS);

const parseTeamIds = (search: string): string[] => {
  const params = new URLSearchParams(search);
  const raw = params.get(QUERY_KEY);
  if (!raw) {
    return [];
  }
  return normalizeTeamIds(raw.split(","));
};

export const teamIdsSearch = (teamIds: string[]): string => {
  const normalized = normalizeTeamIds(teamIds);
  return normalized.length ? `?${QUERY_KEY}=${normalized.join(",")}` : "";
};

const serializeTeamIds = (search: string, teamIds: string[]): string => {
  const params = new URLSearchParams(search);
  const normalized = normalizeTeamIds(teamIds);
  if (!normalized.length) {
    params.delete(QUERY_KEY);
  } else {
    params.set(QUERY_KEY, normalized.join(","));
  }
  const next = params.toString();
  return next ? `?${next}` : "";
};

type SetTeamIds = (nextTeamIds: string[]) => void;

const useUrlTeamIds = (): [string[], SetTeamIds] => {
  const location = useLocation();
  const history = useHistory();

  const teamIds = useMemo(
    () => parseTeamIds(location.search),
    [location.search]
  );

  const setTeamIds = useCallback<SetTeamIds>(
    (nextTeamIds) => {
      const nextSearch = serializeTeamIds(location.search, nextTeamIds);
      if (nextSearch === location.search) {
        return;
      }
      history.replace({
        pathname: location.pathname,
        search: nextSearch,
        hash: location.hash,
      });
    },
    [history, location.hash, location.pathname, location.search]
  );

  return [teamIds, setTeamIds];
};

export default useUrlTeamIds;
